import React, { useState, useEffect } from 'react';
import { 
  TrendingUp, 
  Shield, 
  Zap, 
  Users, 
  BarChart3, 
  ArrowRight,
  Star,
  CheckCircle,
  DollarSign,
  Target,
  Globe,
  Award,
  Play,
  Menu,
  X
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { WebLink } from '../../../utility/customComponents';

const P2PInvestmentHomepage = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const navigate = useNavigate()
  const stats = [
    { value: '$2.4B+', label: 'Total Investment Volume' },
    { value: '150K+', label: 'Active Investors' },
    { value: '12.8%', label: 'Average Annual Returns' },
    { value: '99.2%', label: 'Platform Reliability' }
  ];

  const features = [
    {
      icon: <Shield className="w-8 h-8" />,
      title: 'Bank-Grade Security',
      description: 'Military-grade encryption and multi-layer security protocols protect your investments 24/7.'
    },
    {
      icon: <TrendingUp className="w-8 h-8" />,
      title: 'Smart Analytics',
      description: 'AI-powered insights and real-time market analysis to optimize your investment strategy.'
    },
    {
      icon: <Zap className="w-8 h-8" />,
      title: 'Instant Transactions',
      description: 'Lightning-fast peer-to-peer transfers with minimal fees and maximum efficiency.'
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: 'Verified Network',
      description: 'Connect with thoroughly vetted investors and borrowers in our trusted ecosystem.'
    }
  ];

  const testimonials = [
    {
      name: 'Sarah Chen',
      role: 'Portfolio Manager',
      content: 'The platform\'s AI-driven insights have consistently outperformed traditional investment methods.',
      rating: 5,
      returns: '+24.3%'
    },
    {
      name: 'Marcus Rodriguez',
      role: 'Tech Entrepreneur',
      content: 'Incredible transparency and security. I\'ve been investing here for 3 years with excellent results.',
      rating: 5,
      returns: '+31.7%'
    },
    {
      name: 'Elena Volkov',
      role: 'Real Estate Investor',
      content: 'The peer-to-peer model offers flexibility that traditional banks simply cannot match.',
      rating: 5,
      returns: '+19.8%'
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-800 text-white">
      {/* Navigation */}
      <nav className="relative z-50 px-6 py-4 lg:px-12">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-xl flex items-center justify-center">
              <img src="/logo192.png" />
            </div>
            <span className="text-2xl font-bold bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent">
              OptimalAuction
            </span>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            {/* <WebLink href="#features" className="hover:text-yellow-400 transition-colors">Features</WebLink>
            <a href="#analytics" className="hover:text-yellow-400 transition-colors">Analytics</a>
            <a href="#testimonials" className="hover:text-yellow-400 transition-colors">Success Stories</a>
            <a href="#contact" className="hover:text-yellow-400 transition-colors">Contact</a> */}
          </div>

          <div className="hidden lg:flex items-center space-x-4">
            <button 
              onClick={() => navigate('/signin')} 
              className="px-6 py-2 text-yellow-400 border border-yellow-400 rounded-lg hover:bg-yellow-400 hover:text-black transition-all"
            >
              Sign In
            </button>
            <button 
              onClick={() => navigate('/signup')} 
              className="px-6 py-2 bg-gradient-to-r from-yellow-400 to-orange-500 text-black rounded-lg hover:from-yellow-500 hover:to-orange-600 transition-all font-semibold"
            >
              Get Started
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="lg:hidden"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="lg:hidden absolute top-full left-0 right-0 bg-black/95 backdrop-blur-md border-t border-gray-800">
            <div className="px-6 py-4 space-y-4">
              {/* <a href="#features" className="block py-2 hover:text-yellow-400">Features</a>
              <a href="#analytics" className="block py-2 hover:text-yellow-400">Analytics</a>
              <a href="#testimonials" className="block py-2 hover:text-yellow-400">Success Stories</a>
              <a href="#contact" className="block py-2 hover:text-yellow-400">Contact</a> */}
              <div className="flex flex-col space-y-3 pt-4 border-t border-gray-800">
                <button onClick={() => navigate('/signin')} className="px-6 py-2 text-center text-yellow-400 border border-yellow-400 rounded-lg">
                  Sign In
                </button>
                <button onClick={() => navigate('/signup')} className="px-6 py-2 text-center bg-gradient-to-r from-yellow-400 to-orange-500 text-black rounded-lg font-semibold">
                  Get Started
                </button>
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative px-6 py-20 lg:px-12 lg:py-32">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-yellow-400/20 to-orange-500/20 border border-yellow-400/30 rounded-full px-4 py-2 mb-6">
                <Award className="w-4 h-4 text-yellow-400" />
                <span className="text-sm text-yellow-400">Rated #1 P2P Platform</span>
              </div>
              
              <h1 className="text-5xl lg:text-7xl font-bold mb-6 leading-tight">
                <span className="bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
                  Invest Smarter
                </span>
                <br />
                <span className="bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent">
                  Earn Better
                </span>
              </h1>
              
              <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                Join the future of peer-to-peer investing with advanced AI analytics, 
                transparent returns, and a community of verified investors.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 mb-12">
                <button 
                  onClick={() => navigate('/signup')} 
                  className="inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-yellow-400 to-orange-500 text-black rounded-xl hover:from-yellow-500 hover:to-orange-600 transition-all font-semibold text-lg group"
                >
                  Start Investing Today
                  <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </button>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
                {stats.map((stat, index) => (
                  <div key={index} className="text-center lg:text-left">
                    <div className="text-2xl lg:text-3xl font-bold bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent">
                      {stat.value}
                    </div>
                    <div className="text-sm text-gray-400">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              {/* Dashboard Preview */}
              <div className="relative bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-xl border border-gray-700 rounded-2xl p-6 lg:p-8">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-semibold">Portfolio Overview</h3>
                  <div className="flex items-center space-x-2 text-green-400">
                    <TrendingUp className="w-4 h-4" />
                    <span className="text-sm font-medium">+18.5%</span>
                  </div>
                </div>
                
                {/* Mock Chart */}
                <div className="h-40 bg-gradient-to-r from-yellow-400/10 to-orange-500/10 rounded-lg mb-6 relative overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-t from-yellow-400/20 to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-yellow-400 to-orange-500"></div>
                </div>
                
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-400">$47.2K</div>
                    <div className="text-xs text-gray-400">Total Value</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-yellow-400">12.8%</div>
                    <div className="text-xs text-gray-400">Annual Return</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-400">23</div>
                    <div className="text-xs text-gray-400">Active Loans</div>
                  </div>
                </div>
              </div>

              {/* Floating Elements */}
              <div className="absolute -top-4 -right-4 w-20 h-20 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full opacity-20 animate-pulse"></div>
              <div className="absolute -bottom-8 -left-8 w-32 h-32 bg-gradient-to-r from-blue-400 to-purple-500 rounded-full opacity-10 animate-pulse delay-1000"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="px-6 py-20 lg:px-12 lg:py-32 bg-gradient-to-b from-transparent to-gray-900/50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-6xl font-bold mb-6">
              <span className="bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent">
                Advanced Features
              </span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Experience next-generation P2P investing with cutting-edge technology and unmatched security.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {features.map((feature, index) => (
              <div 
                key={index} 
                className="group p-8 bg-gradient-to-br from-gray-800/30 to-gray-900/30 backdrop-blur-xl border border-gray-700 rounded-2xl hover:border-yellow-400/50 transition-all duration-300 hover:transform hover:scale-105"
              >
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-xl flex items-center justify-center text-black group-hover:scale-110 transition-transform">
                    {feature.icon}
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold mb-3 group-hover:text-yellow-400 transition-colors">
                      {feature.title}
                    </h3>
                    <p className="text-gray-300 leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="px-6 py-20 lg:px-12 lg:py-32">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-6xl font-bold mb-6">
              <span className="bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent">
                Success Stories
              </span>
            </h2>
            <p className="text-xl text-gray-300">
              Real investors, real results, real trust.
            </p>
          </div>

          <div className="relative max-w-4xl mx-auto">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className={`absolute inset-0 transition-opacity duration-500 ${
                  index === activeTestimonial ? 'opacity-100' : 'opacity-0'
                }`}
              >
                <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-xl border border-gray-700 rounded-2xl p-8 lg:p-12 text-center">
                  <div className="flex justify-center mb-6">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-6 h-6 text-yellow-400 fill-current" />
                    ))}
                  </div>
                  
                  <blockquote className="text-2xl lg:text-3xl font-medium mb-8 text-gray-100 leading-relaxed">
                    "{testimonial.content}"
                  </blockquote>
                  
                  <div className="flex items-center justify-center space-x-6">
                    <div>
                      <div className="text-lg font-semibold">{testimonial.name}</div>
                      <div className="text-gray-400">{testimonial.role}</div>
                    </div>
                    <div className="w-px h-12 bg-gray-600"></div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-400">{testimonial.returns}</div>
                      <div className="text-sm text-gray-400">Annual Return</div>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {/* Pagination Dots */}
            <div className="flex justify-center mt-8 space-x-3">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setActiveTestimonial(index)}
                  className={`w-3 h-3 rounded-full transition-all ${
                    index === activeTestimonial 
                      ? 'bg-gradient-to-r from-yellow-400 to-orange-500' 
                      : 'bg-gray-600 hover:bg-gray-500'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="px-6 py-20 lg:px-12 lg:py-32 bg-gradient-to-r from-yellow-400/10 to-orange-500/10">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl lg:text-6xl font-bold mb-6">
            Ready to Start Your
            <br />
            <span className="bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent">
              Investment Journey?
            </span>
          </h2>
          
          <p className="text-xl text-gray-300 mb-12 max-w-2xl mx-auto">
            Join thousands of successful investors who trust OptimalAuction for superior returns and unmatched security.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <button 
              onClick={() => navigate('/signup')} 
              className="inline-flex items-center justify-center px-10 py-5 bg-gradient-to-r from-yellow-400 to-orange-500 text-black rounded-xl hover:from-yellow-500 hover:to-orange-600 transition-all font-bold text-xl group"
            >
              Start Investing Now
              <ArrowRight className="w-6 h-6 ml-3 group-hover:translate-x-1 transition-transform" />
            </button>
            
            <button 
              onClick={() => navigate('/signin')} 
              className="inline-flex items-center justify-center px-10 py-5 border-2 border-yellow-400 text-yellow-400 rounded-xl hover:bg-yellow-400 hover:text-black transition-all font-bold text-xl"
            >
              Sign In to Dashboard
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="px-6 py-12 lg:px-12 bg-black/50 border-t border-gray-800">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row justify-between items-center">
            <div className="flex items-center space-x-3 mb-6 lg:mb-0">
              <div className="w-8 h-8 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-lg flex items-center justify-center">
                <BarChart3 className="w-5 h-5 text-black" />
              </div>
              <span className="text-xl font-bold bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent">
                OptimalAuction
              </span>
            </div>
            
            <div className="text-center lg:text-right text-gray-400">
              <p>&copy; 2025 OptimalAuction. All rights reserved.</p>
              <p className="text-sm mt-1">Investing involves risk. Past performance does not guarantee future results.</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default P2PInvestmentHomepage;